import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { openDemoBookingModal, openEliteBuyerModal } from '../utils/functions';
import apiService from '../services/api';

const GRADE_OPTIONS = ['PVC', 'HDPE', 'LDPE', 'LLDPE', 'PP', 'PET', 'ABS', 'Taita', 'EVAPC', 'GPPS', 'HIPS', 'EPS', 'Nylon 6', 'Other'];
const BUSINESS_TYPE_OPTIONS = ['Importer', 'Manufacturer', 'Trader', 'Stockist'];

const EliteBuyerClub = () => {
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    preferred_grade: [],
    business_type: [],
    city: '',
    mobile: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isGradeDropdownOpen, setIsGradeDropdownOpen] = useState(false);
  const [isBusinessTypeDropdownOpen, setIsBusinessTypeDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const gradeDropdownRef = useRef(null);
  const businessTypeDropdownRef = useRef(null);
  const dropdownPortalRef = useRef(null);

  const updateDropdownPosition = () => {
    const ref = isGradeDropdownOpen ? gradeDropdownRef.current : businessTypeDropdownRef.current;
    if (ref) {
      const rect = ref.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  };

  useLayoutEffect(() => {
    if (isGradeDropdownOpen || isBusinessTypeDropdownOpen) {
      updateDropdownPosition();
    }
  }, [isGradeDropdownOpen, isBusinessTypeDropdownOpen]);

  useEffect(() => {
    if (!isGradeDropdownOpen && !isBusinessTypeDropdownOpen) return;
    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [isGradeDropdownOpen, isBusinessTypeDropdownOpen]);

  // Close dropdowns when clicking outside (trigger or portal)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inGrade = gradeDropdownRef.current && gradeDropdownRef.current.contains(event.target);
      const inBusiness = businessTypeDropdownRef.current && businessTypeDropdownRef.current.contains(event.target);
      const inPortal = dropdownPortalRef.current && dropdownPortalRef.current.contains(event.target);
      if (inPortal) return;
      if (isGradeDropdownOpen && !inGrade) setIsGradeDropdownOpen(false);
      if (isBusinessTypeDropdownOpen && !inBusiness) setIsBusinessTypeDropdownOpen(false);
    };

    if (isGradeDropdownOpen || isBusinessTypeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isGradeDropdownOpen, isBusinessTypeDropdownOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Mobile number validation - only digits, max 10
    if (name === 'mobile') {
      const mobileValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      setFormData({
        ...formData,
        [name]: mobileValue
      });
      if (errors.mobile) {
        setErrors({ ...errors, mobile: '' });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleGradeToggle = (grade) => {
    setFormData(prev => {
      const isSelected = prev.preferred_grade.includes(grade);
      const newGrades = isSelected
        ? prev.preferred_grade.filter(g => g !== grade)
        : [...prev.preferred_grade, grade];
      
      return { ...prev, preferred_grade: newGrades };
    });
  };

  const handleBusinessTypeToggle = (type) => {
    setFormData(prev => {
      const isSelected = prev.business_type.includes(type);
      const newTypes = isSelected
        ? prev.business_type.filter(t => t !== type)
        : [...prev.business_type, type];
      
      return { ...prev, business_type: newTypes };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Email validation
    if (!formData.email || !formData.email.trim()) {
      newErrors.email = 'Please enter your email address';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Mobile validation - exactly 10 digits
    if (!formData.mobile || !formData.mobile.trim()) {
      newErrors.mobile = 'Please enter your mobile number';
    } else if (!/^[0-9]{10}$/.test(formData.mobile)) {
      newErrors.mobile = 'Mobile number must be exactly 10 digits';
    }
    
    // Business Type validation - at least one required
    if (!formData.business_type || formData.business_type.length === 0) {
      newErrors.business_type = 'Please select at least one business type';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    const submitFormData = new FormData();
    submitFormData.append('name', formData.name);
    submitFormData.append('company_name', formData.company_name);
    submitFormData.append('city', formData.city);
    submitFormData.append('mobile', formData.mobile);
    submitFormData.append('email', formData.email);
    
    // Handle arrays for multiselect
    formData.preferred_grade.forEach(grade => {
      submitFormData.append('preferred_grade[]', grade);
    });
    formData.business_type.forEach(type => {
      submitFormData.append('business_type[]', type);
    });
    
    submitFormData.append('form_type', 'elite_buyer_club');

    try {
      const result = await apiService.submitEliteBuyer(submitFormData);
      if (result.success) {
        alert('Thank you! Your Elite Buyer Club application has been submitted successfully. Our team will contact you shortly.');
        setFormData({
          name: '',
          company_name: '',
          preferred_grade: [],
          business_type: [],
          city: '',
          mobile: '',
          email: ''
        });
        setErrors({});
        setIsGradeDropdownOpen(false);
        setIsBusinessTypeDropdownOpen(false);
      } else {
        alert(result.message || 'Error submitting form. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="elite-buyer-page">
      <section className="elite-hero section-padding">
        <div className="container">
          <div className="elite-hero-card-wrapper">
            <div className="elite-hero-content">
              <span className="elite-badge"><i className="fas fa-star"></i> Exclusive Syndicate</span>
              <h1>Elite Buyer Club – Prime Polymer Division</h1>
              <p className="elite-subtitle">Exclusive Syndicate for Serious Buyers & Importers</p>
              <p className="elite-description">
                Elite Buyer Club is a high-value, invitation-only community created by <strong>BSB Supply Chain</strong> to bring together leading <strong>Domestic Polymer Buyers and Importers</strong> into a unified, technology-enabled syndicate.
              </p>
              <div className="elite-mission">
                <p><strong>The objective is simple:</strong> Collective Strength → Better Prices → Higher Efficiency → Smarter Trading.</p>
              </div>
              <div className="elite-actions">
                <button className="btn btn-primary" onClick={() => openEliteBuyerModal()}>
                  <i className="fas fa-rocket"></i>
                  <span>Join The Club</span>
                </button>
                <a className="btn btn-outline elite-whatsapp" href="https://wa.me/919825555402?text=Hi%20Team%20BSB%2C%20I%20want%20to%20join%20the%20Elite%20Buyer%20Club." target="_blank" rel="noopener">
                  <i className="fab fa-whatsapp"></i>
                  <span>Talk on WhatsApp</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="elite-benefits-section section-padding">
        <div className="container">
          <div className="section-header">
            <span className="section-badge"><i className="fas fa-star"></i> Benefits</span>
            <h2>Why Join the Elite Buyer Club?</h2>
            <p>Discover the exclusive advantages that make Elite Buyer Club the preferred choice for serious polymer buyers and importers</p>
          </div>
          <div className="elite-benefits-grid">
            {[
              { icon: 'fa-users', title: 'Syndicate Buying Power', desc: 'Pooling purchasing volumes of multiple credible buyers helps achieve better negotiation, improved terms, and priority allocation from suppliers and importers.' },
              { icon: 'fa-tags', title: 'Bulk Price Advantage', desc: 'Through consolidated monthly demand planning, members enjoy special bulk-rate pricing, discounts, and preferred deals across Prime Polymers.' },
              { icon: 'fa-chart-line', title: 'Lower Operational Costs', desc: 'Optimised logistics, shared intelligence, coordinated procurement cycles, and BSB-managed backend operations reduce overall procurement and handling costs.' },
              { icon: 'fa-cog', title: 'End-to-End Elite Buyer Management', desc: 'BSB handles the entire ecosystem—supplier coordination, documentation, sourcing schedules, rate comparison, and logistics—ensuring hassle-free operations for members.' },
              { icon: 'fa-calendar-alt', title: 'Exclusive Monthly Meets', desc: 'Closed-group meets for members to review market insights, rate trends, sourcing opportunities, and monthly volume planning.' },
              { icon: 'fa-lightbulb', title: 'Knowledge & Know-How Sharing', desc: 'Members get continuous access to industry insights, global trends, IRM data points, market intelligence, and supplier updates—empowering smarter trade decisions.' }
            ].map((benefit, index) => (
              <article key={index} className="elite-benefit-card">
                <div className="benefit-icon">
                  <i className={`fas ${benefit.icon}`}></i>
                </div>
                <h3>{benefit.title}</h3>
                <p>{benefit.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="elite-registration-section section-padding bg-light">
        <div className="container">
          <div className="section-header">
            <h2>Apply for Membership</h2>
            <p>Fill out the form below to apply for Elite Buyer Club membership. Our team will review your application and contact you within 24 hours.</p>
          </div>
          
          <div className="registration-form-container">
            <form className="elite-registration-form elite-form-vertical" onSubmit={handleSubmit}>
              <input type="hidden" name="form_type" value="elite_buyer_club" />
              
              <div className="form-group">
                <label htmlFor="elite-name">Name <span className="required">*</span></label>
                <input 
                  type="text" 
                  name="name" 
                  id="elite-name" 
                  required 
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="elite-company">Company Name <span className="required">*</span></label>
                <input 
                  type="text" 
                  name="company_name" 
                  id="elite-company" 
                  required 
                  placeholder="Enter your company name"
                  value={formData.company_name}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="elite-grade">Preferred Grade</label>
                <div className="custom-multi-select" ref={gradeDropdownRef}>
                  <button
                    type="button"
                    className={`custom-select-trigger ${isGradeDropdownOpen ? 'open' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsBusinessTypeDropdownOpen(false);
                      setIsGradeDropdownOpen(!isGradeDropdownOpen);
                    }}
                    aria-haspopup="listbox"
                    aria-expanded={isGradeDropdownOpen}
                  >
                    <span className="select-placeholder">
                      {formData.preferred_grade.length > 0 
                        ? `${formData.preferred_grade.length} grade${formData.preferred_grade.length > 1 ? 's' : ''} selected`
                        : 'Select Preferred Grade'
                      }
                    </span>
                    <span className="select-arrow">
                      <i className={`fas fa-chevron-${isGradeDropdownOpen ? 'up' : 'down'}`}></i>
                    </span>
                  </button>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="elite-type">Business Type <span className="required">*</span></label>
                <div className="custom-multi-select" ref={businessTypeDropdownRef}>
                  <button
                    type="button"
                    className={`custom-select-trigger ${isBusinessTypeDropdownOpen ? 'open' : ''} ${errors.business_type ? 'error' : ''} ${formData.business_type.length > 0 ? 'has-selection' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsGradeDropdownOpen(false);
                      setIsBusinessTypeDropdownOpen(!isBusinessTypeDropdownOpen);
                    }}
                    aria-haspopup="listbox"
                    aria-expanded={isBusinessTypeDropdownOpen}
                  >
                    <span className="select-placeholder">
                      {formData.business_type.length > 0 
                        ? `${formData.business_type.length} type${formData.business_type.length > 1 ? 's' : ''} selected`
                        : 'Select Business Type'
                      }
                    </span>
                    <span className="select-arrow">
                      <i className={`fas fa-chevron-${isBusinessTypeDropdownOpen ? 'up' : 'down'}`}></i>
                    </span>
                  </button>
                </div>
                {errors.business_type && <span className="error-message">{errors.business_type}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="elite-city">City <span className="required">*</span></label>
                <input 
                  type="text" 
                  name="city" 
                  id="elite-city" 
                  required 
                  placeholder="e.g., Ahmedabad, Gujarat"
                  value={formData.city}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="elite-mobile">Mobile No. <span className="required">*</span></label>
                <input 
                  type="tel" 
                  name="mobile" 
                  id="elite-mobile" 
                  required 
                  pattern="[0-9]{10}" 
                  placeholder="Enter 10-digit mobile number" 
                  maxLength={10}
                  inputMode="numeric"
                  value={formData.mobile}
                  onChange={handleChange}
                />
                {errors.mobile && <span className="error-message">{errors.mobile}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="elite-email">Email Id <span className="required">*</span></label>
                <input 
                  type="email" 
                  name="email" 
                  id="elite-email" 
                  required 
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={handleChange}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              
              <div className="form-submit">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i> Submit Application
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Portal dropdowns – render into document.body so they're never transparent */}
      {(isGradeDropdownOpen || isBusinessTypeDropdownOpen) && dropdownPosition.width > 0 &&
        createPortal(
          <div
            ref={dropdownPortalRef}
            className="elite-dropdown-portal"
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              zIndex: 999999,
              backgroundColor: '#ffffff',
              background: '#ffffff',
              border: '1px solid rgba(5, 28, 63, 0.06)',
              borderRadius: '6px',
              boxShadow: '0 20px 40px rgba(5, 28, 63, 0.18)',
              maxHeight: 280,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <div className="dropdown-list" style={{ padding: '6px 0', margin: 0, backgroundColor: '#ffffff' }}>
              {isGradeDropdownOpen &&
                GRADE_OPTIONS.map((grade, index) => (
                  <label key={index} className="dropdown-option" style={{ backgroundColor: '#ffffff', color: '#333' }}>
                    <input
                      type="checkbox"
                      checked={formData.preferred_grade.includes(grade)}
                      onChange={() => handleGradeToggle(grade)}
                    />
                    <span className="checkbox-label">{grade}</span>
                  </label>
                ))}
              {isBusinessTypeDropdownOpen &&
                BUSINESS_TYPE_OPTIONS.map((type, index) => (
                  <label key={index} className="dropdown-option" style={{ backgroundColor: '#ffffff', color: '#333' }}>
                    <input
                      type="checkbox"
                      checked={formData.business_type.includes(type)}
                      onChange={() => handleBusinessTypeToggle(type)}
                    />
                    <span className="checkbox-label">{type}</span>
                  </label>
                ))}
            </div>
          </div>,
          document.body
        )}
    </main>
  );
};

export default EliteBuyerClub;

